import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adminAPI } from '../services/api';
import { AmiDashboard, AmiCdrDebugResult, AmiSettings } from '../types';
import { extractApiError } from '../utils/extractApiError';
import { canAccessAmi, isMainAdmin } from '../utils/amiAccess';

interface AmiSettingsForm {
  host: string;
  port: number;
  username: string;
  secret: string;
  enabled: boolean;
  queueLoginEnabled: boolean;
  queueLoginNumber: string;
  secretConfigured: boolean;
  is_configured: boolean;
  cdrDbUser: string;
  cdrDbName: string;
  cdrDbSecret: string;
  cdrSecretConfigured: boolean;
  cdrIsConfigured: boolean;
}

const defaultFormState: AmiSettingsForm = {
  host: '',
  port: 5038,
  username: '',
  secret: '',
  enabled: false,
  queueLoginEnabled: false,
  queueLoginNumber: '800',
  secretConfigured: false,
  is_configured: false,
  cdrDbUser: '',
  cdrDbName: 'asteriskcdrdb',
  cdrDbSecret: '',
  cdrSecretConfigured: false,
  cdrIsConfigured: false,
};

const REFRESH_INTERVAL_MS = 15000;

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins <= 0) return `${secs} ثانیه`;
  return `${mins} دقیقه و ${secs} ثانیه`;
}

function statusBadgeClass(label: string): string {
  const normalized = label.trim();
  if (['آماده', 'برقرار'].includes(normalized)) return 'bg-green-100 text-green-800';
  if (['مشغول', 'در حال استفاده'].includes(normalized)) return 'bg-amber-100 text-amber-800';
  if (['زنگ می‌خورد', 'در حال زنگ', 'در حال زنگ در صف'].includes(normalized)) return 'bg-blue-100 text-blue-800';
  if (['آفلاین', 'قطع', 'در دسترس نیست'].includes(normalized)) return 'bg-gray-100 text-gray-700';
  return 'bg-slate-100 text-slate-800';
}

const AdminAmiSettings: React.FC = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<AmiSettingsForm>(defaultFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [dashboard, setDashboard] = useState<AmiDashboard | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [debugPhone, setDebugPhone] = useState('');
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState<AmiCdrDebugResult | null>(null);
  const canManageSettings = isMainAdmin();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');

    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (!canAccessAmi()) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await adminAPI.getAmiSettings();
        setFormState({
          host: data.host || '',
          port: data.port ?? 5038,
          username: data.username || '',
          secret: '',
          enabled: data.enabled,
          queueLoginEnabled: Boolean(data.queue_login_enabled),
          queueLoginNumber: data.queue_login_number || '800',
          secretConfigured: data.secret_configured,
          is_configured: data.is_configured,
          cdrDbUser: data.cdr_db_user || '',
          cdrDbName: data.cdr_db_name || 'asteriskcdrdb',
          cdrDbSecret: '',
          cdrSecretConfigured: Boolean(data.cdr_secret_configured),
          cdrIsConfigured: Boolean(data.cdr_is_configured),
        });
      } catch (err: unknown) {
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'خطا در دریافت تنظیمات AMI');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const fetchDashboard = useCallback(async (silent = false) => {
    if (!silent) setDashboardLoading(true);
    setDashboardError(null);
    try {
      const { data } = await adminAPI.getAmiDashboard();
      setDashboard(data);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setDashboardError(typeof detail === 'string' ? detail : 'خطا در دریافت وضعیت زنده مرکز تلفن');
    } finally {
      if (!silent) setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!autoRefresh || (!formState.enabled && !formState.is_configured)) return undefined;
    const timer = window.setInterval(() => fetchDashboard(true), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [autoRefresh, fetchDashboard, formState.enabled, formState.is_configured]);

  const handleCdrDebug = async () => {
    const phone = debugPhone.trim();
    if (!phone) return;
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const { data } = await adminAPI.debugAmiCdr(phone);
      setDebugResult(data);
    } catch (err: unknown) {
      const { message, output } = extractApiError(err);
      setDebugResult({
        phone,
        search_keys: [],
        cdr_configured: formState.cdrIsConfigured || formState.cdrSecretConfigured,
        ami_enabled: formState.enabled,
        ami_host: formState.host || null,
        success: false,
        records: [],
        logs: [
          {
            step: 'api_error',
            ok: false,
            message,
            duration_ms: 0,
            output,
          },
        ],
      });
    } finally {
      setDebugLoading(false);
    }
  };

  const handleChange = (field: keyof AmiSettingsForm, value: string | number | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field !== 'enabled') {
      setTestResult(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canManageSettings) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    const payload: Partial<AmiSettings> & { secret?: string; cdr_db_secret?: string } = {
      host: formState.host.trim(),
      port: formState.port,
      username: formState.username.trim(),
      enabled: formState.enabled,
      queue_login_enabled: formState.queueLoginEnabled,
      queue_login_number: formState.queueLoginNumber.trim() || '800',
      cdr_db_user: formState.cdrDbUser.trim() || null,
      cdr_db_name: formState.cdrDbName.trim() || 'asteriskcdrdb',
    };

    if (formState.secret.trim()) {
      payload.secret = formState.secret.trim();
    }
    if (formState.cdrDbSecret.trim()) {
      payload.cdr_db_secret = formState.cdrDbSecret.trim();
    }

    try {
      const { data } = await adminAPI.updateAmiSettings(payload);
      setFormState((prev) => ({
        ...prev,
        secret: '',
        cdrDbSecret: '',
        secretConfigured: data.secret_configured,
        cdrSecretConfigured: Boolean(data.cdr_secret_configured),
        cdrIsConfigured: Boolean(data.cdr_is_configured),
        enabled: data.enabled,
        queueLoginEnabled: Boolean(data.queue_login_enabled),
        queueLoginNumber: data.queue_login_number || '800',
      }));
      setSuccessMessage('تنظیمات AMI با موفقیت ذخیره شد');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    const payload: {
      host?: string;
      port?: number;
      username?: string;
      secret?: string;
    } = {
      host: formState.host.trim() || undefined,
      port: formState.port || undefined,
      username: formState.username.trim() || undefined,
    };

    if (formState.secret.trim()) {
      payload.secret = formState.secret.trim();
    }

    try {
      const { data } = await adminAPI.testAmiSettings(payload);
      setTestResult({ success: data.success, message: data.message });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setTestResult({
        success: false,
        message: typeof detail === 'string' ? detail : 'تست اتصال AMI با خطا مواجه شد',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner text="در حال بارگذاری تنظیمات AMI..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">مرکز تلفن (AMI)</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              اتصال Asterisk Manager Interface به Issabel از سرور بک‌اند پنل (پورت TCP 5038).
              {!canManageSettings && ' شما فقط می‌توانید وضعیت اتصال را ببینید و تست کنید.'}
            </p>
          </div>

          {!canManageSettings && (
            <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              تنظیمات اتصال فقط توسط <strong>مدیر کل</strong> قابل ویرایش است. برای تغییر host یا رمز AMI با مدیر کل هماهنگ کنید.
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" style={{ direction: 'rtl' }}>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">اطلاعات اتصال AMI</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    آدرس سرور Issabel <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formState.host}
                    onChange={(e) => handleChange('host', e.target.value)}
                    readOnly={!canManageSettings}
                    className={`w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canManageSettings ? 'bg-gray-100' : ''}`}
                    placeholder="مثال: 192.168.1.50"
                    required={canManageSettings}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">پورت AMI</label>
                  <input
                    type="number"
                    min={1}
                    max={65535}
                    value={formState.port}
                    onChange={(e) => handleChange('port', Number(e.target.value) || 5038)}
                    readOnly={!canManageSettings}
                    className={`w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canManageSettings ? 'bg-gray-100' : ''}`}
                  />
                  <p className="mt-1 text-xs text-gray-500">پیش‌فرض Asterisk: 5038</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نام کاربری AMI <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formState.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    readOnly={!canManageSettings}
                    className={`w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canManageSettings ? 'bg-gray-100' : ''}`}
                    placeholder="مثال: admin"
                    required={canManageSettings}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رمز عبور AMI (Secret)
                  </label>
                  {canManageSettings ? (
                  <input
                    type="password"
                    value={formState.secret}
                    onChange={(e) => handleChange('secret', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={formState.secretConfigured ? 'برای تغییر، رمز جدید وارد کنید' : 'رمز Secret از Issabel'}
                  />
                  ) : (
                    <p className="text-sm text-gray-600 bg-gray-100 rounded-md px-3 py-2">
                      {formState.secretConfigured ? 'رمز ذخیره شده (مخفی)' : 'تنظیم نشده'}
                    </p>
                  )}
                  {formState.secretConfigured && !formState.secret && canManageSettings && (
                    <p className="mt-1 text-xs text-green-700">رمز عبور قبلاً ذخیره شده است.</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="ami-enabled"
                  type="checkbox"
                  checked={formState.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                  disabled={!canManageSettings}
                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <label htmlFor="ami-enabled" className="text-sm font-medium text-gray-800 cursor-pointer">
                  اتصال AMI فعال باشد
                </label>
              </div>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-6">
              <h3 className="text-lg font-semibold text-gray-800">ورود خودکار به صف (Queue Login)</h3>

              <div className="flex items-center gap-3">
                <input
                  id="queue-login-enabled"
                  type="checkbox"
                  checked={formState.queueLoginEnabled}
                  onChange={(e) => handleChange('queueLoginEnabled', e.target.checked)}
                  disabled={!canManageSettings || !formState.enabled}
                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <label htmlFor="queue-login-enabled" className="text-sm font-medium text-gray-800 cursor-pointer">
                  سیستم ورود به صف هنگام لاگین فعال باشد
                </label>
              </div>

              <div className="max-w-xs">
                <label className="mb-1 block text-sm font-medium text-gray-700">شماره صف</label>
                {canManageSettings ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formState.queueLoginNumber}
                    onChange={(e) =>
                      handleChange('queueLoginNumber', e.target.value.replace(/\D/g, '').slice(0, 10))
                    }
                    disabled={!formState.queueLoginEnabled}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="800"
                  />
                ) : (
                  <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
                    {formState.queueLoginNumber || '800'}
                  </p>
                )}
              </div>
            </section>

            {canManageSettings && (
            <section className="space-y-4 border-t border-gray-100 pt-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">تاریخچه تماس (CDR)</h3>
                <p className="text-sm text-gray-600 mt-1">
                  برای نمایش تماس‌های واقعی در جزئیات نوبت، اطلاعات دیتابیس CDR Issabel 5 را وارد کنید.
                  معمولاً در فایل <code className="text-xs bg-gray-100 px-1 rounded">/etc/issabel.conf</code> موجود است.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری دیتابیس CDR</label>
                  <input
                    type="text"
                    value={formState.cdrDbUser}
                    onChange={(e) => handleChange('cdrDbUser', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="مثال: asteriskuser"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام دیتابیس CDR</label>
                  <input
                    type="text"
                    value={formState.cdrDbName}
                    onChange={(e) => handleChange('cdrDbName', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="asteriskcdrdb"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">رمز دیتابیس CDR</label>
                  <input
                    type="password"
                    value={formState.cdrDbSecret}
                    onChange={(e) => handleChange('cdrDbSecret', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={formState.cdrSecretConfigured ? 'برای تغییر، رمز جدید وارد کنید' : 'AMPDBPASS از /etc/issabel.conf'}
                  />
                  {formState.cdrSecretConfigured && !formState.cdrDbSecret && (
                    <p className="mt-1 text-xs text-green-700">رمز CDR قبلاً ذخیره شده است.</p>
                  )}
                </div>
              </div>
            </section>
            )}

            <div className="flex flex-wrap gap-3">
              {canManageSettings && (
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
              </button>
              )}
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={testing || !formState.host.trim() || !formState.username.trim()}
                className="px-5 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {testing ? 'در حال تست...' : 'تست اتصال AMI'}
              </button>
            </div>

            {testResult && (
              <div
                className={`p-4 rounded-xl text-sm whitespace-pre-line ${
                  testResult.success
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {testResult.message}
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-8 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">دیباگ تاریخچه تماس (CDR)</h2>
            <p className="text-sm text-gray-600 mt-1">
              CDR با اتصال مستقیم <strong>MySQL (پورت 3306)</strong> خوانده می‌شود — فقط SELECT.
              AMI فقط برای داشبورد زنده است. نصب اسکریپت روی Issabel لازم نیست.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className={`px-2 py-1 rounded ${formState.host ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                AMI host: {formState.host || 'تنظیم نشده'}
              </span>
              <span className={`px-2 py-1 rounded ${formState.cdrSecretConfigured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                رمز CDR: {formState.cdrSecretConfigured ? 'ذخیره شده' : 'ذخیره نشده'}
              </span>
              <span className={`px-2 py-1 rounded ${formState.cdrIsConfigured ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                CDR کامل: {formState.cdrIsConfigured ? 'بله' : 'خیر — کاربر/رمز CDR را ذخیره کنید'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">شماره موبایل</label>
              <input
                type="text"
                value={debugPhone}
                onChange={(e) => setDebugPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCdrDebug()}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="مثال: 09124152706"
                dir="ltr"
              />
            </div>
            <button
              type="button"
              onClick={handleCdrDebug}
              disabled={debugLoading || !debugPhone.trim()}
              className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            >
              {debugLoading ? 'در حال جستجو...' : 'جستجوی CDR + لاگ'}
            </button>
          </div>

          {debugResult && (
            <div className="space-y-4">
              <div
                className={`rounded-xl border p-4 text-sm ${
                  debugResult.success
                    ? 'border-green-200 bg-green-50 text-green-900'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
              >
                {debugResult.success
                  ? `${debugResult.records.length} تماس CDR پیدا شد`
                  : 'تماسی پیدا نشد — لاگ زیر را بررسی کنید'}
                {debugResult.search_keys.length > 0 && (
                  <p className="mt-2 text-xs opacity-80">
                    کلیدهای جستجو: {debugResult.search_keys.join(' · ')}
                  </p>
                )}
                <p className="mt-1 text-xs opacity-80">
                  AMI: {debugResult.ami_host || '—'} · CDR تنظیم شده: {debugResult.cdr_configured ? 'بله' : 'خیر'}
                </p>
              </div>

              {debugResult.records.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-right font-medium">زمان</th>
                        <th className="px-4 py-3 text-right font-medium">مبدأ</th>
                        <th className="px-4 py-3 text-right font-medium">مقصد</th>
                        <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                        <th className="px-4 py-3 text-right font-medium">مدت (ثانیه)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {debugResult.records.map((rec) => (
                        <tr key={rec.unique_id || `${rec.call_time}-${rec.src}-${rec.dst}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-xs" dir="ltr">{rec.call_time}</td>
                          <td className="px-4 py-3 font-medium" dir="ltr">{rec.src}</td>
                          <td className="px-4 py-3" dir="ltr">{rec.dst}</td>
                          <td className="px-4 py-3">{rec.disposition}</td>
                          <td className="px-4 py-3">{rec.billsec}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-800">لاگ گام‌ها</h3>
                {debugResult.logs.map((log, idx) => (
                  <div
                    key={`${log.step}-${idx}`}
                    className={`rounded-lg border px-4 py-3 text-sm ${
                      log.ok ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono text-xs text-gray-600">{log.step}</span>
                      <span className="text-xs text-gray-500">{log.duration_ms} ms</span>
                    </div>
                    <p className={`mt-1 ${log.ok ? 'text-green-900' : 'text-red-800'}`}>{log.message}</p>
                    {log.output.length > 0 && (
                      <pre className="mt-2 text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto" dir="ltr">
                        {log.output.join('\n')}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-8 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900">وضعیت زنده مرکز تلفن</h2>
              <p className="text-sm text-gray-600 mt-1">
                تماس‌های فعال، داخلی‌ها و صف‌ها مستقیماً از Asterisk خوانده می‌شوند.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                بروزرسانی خودکار (۱۵ ثانیه)
              </label>
              <button
                type="button"
                onClick={() => fetchDashboard()}
                disabled={dashboardLoading || (!formState.enabled && !formState.is_configured)}
                className="px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                {dashboardLoading ? 'در حال بروزرسانی...' : 'بروزرسانی'}
              </button>
            </div>
          </div>

          {!formState.enabled && !formState.is_configured && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              برای مشاهده وضعیت زنده، ابتدا اتصال AMI را فعال و تنظیمات را ذخیره کنید.
            </div>
          )}

          {!dashboard && !dashboardLoading && !dashboardError && formState.is_configured && (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
              برای مشاهده وضعیت زنده، دکمه «بروزرسانی» را بزنید.
            </p>
          )}

          {dashboardError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {dashboardError}
            </div>
          )}

          {dashboard?.warnings && dashboard.warnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
              {dashboard.warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          )}

          {dashboard && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm text-emerald-800">تماس فعال</p>
                  <p className="text-3xl font-bold text-emerald-900 mt-1">{dashboard.active_calls.length}</p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm text-blue-800">داخلی</p>
                  <p className="text-3xl font-bold text-blue-900 mt-1">{dashboard.extensions.length}</p>
                </div>
                <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                  <p className="text-sm text-violet-800">صف</p>
                  <p className="text-3xl font-bold text-violet-900 mt-1">{dashboard.queues.length}</p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800">تماس‌های فعال</h3>
                {dashboard.active_calls.length === 0 ? (
                  <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">در حال حاضر تماس فعالی ثبت نشده است.</p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-right font-medium">تماس‌گیرنده</th>
                          <th className="px-4 py-3 text-right font-medium">مقصد</th>
                          <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                          <th className="px-4 py-3 text-right font-medium">مدت</th>
                          <th className="px-4 py-3 text-right font-medium">کانال</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dashboard.active_calls.map((call) => (
                          <tr key={call.unique_id || call.channel} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{call.caller_id_num || '—'}</div>
                              {call.caller_id_name && <div className="text-gray-500 text-xs">{call.caller_id_name}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <div>{call.connected_line_num || call.extension || '—'}</div>
                              {call.connected_line_name && <div className="text-gray-500 text-xs">{call.connected_line_name}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(call.state_label)}`}>
                                {call.state_label}
                              </span>
                            </td>
                            <td className="px-4 py-3">{formatDuration(call.duration_seconds)}</td>
                            <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate" title={call.channel}>
                              {call.channel}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800">لیست داخلی‌ها</h3>
                {dashboard.extensions.length === 0 ? (
                  <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">داخلی‌ای از Asterisk دریافت نشد.</p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-right font-medium">داخلی</th>
                          <th className="px-4 py-3 text-right font-medium">فناوری</th>
                          <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                          <th className="px-4 py-3 text-right font-medium">آدرس / تماس</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dashboard.extensions.map((ext) => (
                          <tr key={`${ext.technology}-${ext.name}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{ext.name}</td>
                            <td className="px-4 py-3 uppercase text-gray-600">{ext.technology}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClass(ext.status_label)}`}>
                                {ext.status_label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-xs max-w-[240px] truncate" title={ext.address || ''}>
                              {ext.address || (ext.active_channels != null ? `${ext.active_channels} کانال فعال` : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800">وضعیت صف‌ها</h3>
                {dashboard.queues.length === 0 ? (
                  <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">صفی در Asterisk پیدا نشد.</p>
                ) : (
                  <div className="space-y-4">
                    {dashboard.queues.map((queue) => (
                      <div key={queue.name} className="rounded-xl border border-gray-200 overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">صف {queue.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">
                              استراتژی: {queue.strategy || '—'} · در انتظار: {queue.calls_waiting} · تکمیل: {queue.completed} · رها شده: {queue.abandoned}
                            </p>
                          </div>
                          <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                            queue.calls_waiting > 0 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {queue.calls_waiting > 0 ? `${queue.calls_waiting} تماس در صف` : 'بدون انتظار'}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                          <div className="p-4">
                            <p className="text-sm font-medium text-gray-800 mb-2">اعضای صف ({queue.members.length})</p>
                            {queue.members.length === 0 ? (
                              <p className="text-xs text-gray-500">عضوی ثبت نشده</p>
                            ) : (
                              <ul className="space-y-2">
                                {queue.members.map((member) => (
                                  <li key={`${queue.name}-${member.name}-${member.location}`} className="flex items-center justify-between gap-2 text-sm">
                                    <div>
                                      <span className="font-medium text-gray-900">{member.name}</span>
                                      {member.location && <span className="text-gray-500 text-xs mr-2"> ({member.location})</span>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {member.paused && (
                                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">مکث</span>
                                      )}
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(member.status_label)}`}>
                                        {member.status_label}
                                      </span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="p-4">
                            <p className="text-sm font-medium text-gray-800 mb-2">تماس‌های در انتظار ({queue.callers.length})</p>
                            {queue.callers.length === 0 ? (
                              <p className="text-xs text-gray-500">تماسی در صف نیست</p>
                            ) : (
                              <ul className="space-y-2">
                                {queue.callers.map((caller) => (
                                  <li key={`${queue.name}-${caller.channel}-${caller.position}`} className="flex items-center justify-between gap-2 text-sm">
                                    <div>
                                      <span className="font-medium text-gray-900">{caller.caller_id_num || 'نامشخص'}</span>
                                      {caller.caller_id_name && <span className="text-gray-500 text-xs mr-2"> ({caller.caller_id_name})</span>}
                                    </div>
                                    <span className="text-xs text-gray-600">
                                      نوبت {caller.position} · {formatDuration(caller.wait_seconds)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {dashboard.fetched_at && (
                <p className="text-xs text-gray-500">
                  آخرین بروزرسانی: {new Date(dashboard.fetched_at).toLocaleString('fa-IR')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAmiSettings;
